import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import FileDropzone, { PITAX_ACCEPTED } from '../components/import/FileDropzone';
import { IMPORT_ROLES } from '../components/import/ImportLink';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { columnLetter, downloadText, templateCsv } from '../utils/importTemplate';

/*
 * Bulk import from Excel or CSV (routes/bulk_import.py):
 *   1. choose what you are importing, download a template if you want one, drop the file
 *   2. check which column holds which field (matched from the headers, in English or Hebrew)
 *   3. check every row: problems are listed by spreadsheet row, and nothing is saved yet
 *   4. import -- all rows or none -- and undo it while nothing depends on it yet
 *
 * "pitax" takes PITAX's export instead of a spreadsheet the lab filled in:
 * its .zip package, or the PITAX_export_tables.xlsx inside it. The server
 * reads the tables it needs and hands back one row per consensus sequence
 * (routes/pitax_import.py), so there is no sheet to choose; each row adds an
 * identification to the DNA isolate PITAX named (dna_identifications, 0051).
 */
const KINDS = ['plates', 'slants', 'dna', 'pitax'];

export default function ImportData() {
  const { t, i18n } = useTranslation('import');
  const tRef = useRef(t);
  tRef.current = t;
  const { user } = useAuth();
  const allowed = (user?.roles || []).some(r => IMPORT_ROLES.includes(r));
  const [params, setParams] = useSearchParams();
  const kind = KINDS.includes(params.get('kind')) ? params.get('kind') : 'plates';
  const isPitax = kind === 'pitax';
  const he = i18n.language === 'he';

  const [fields, setFields] = useState([]);
  const [file, setFile] = useState(null);
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [createMissing, setCreateMissing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoError, setUndoError] = useState('');

  const problemText = useCallback((p) => tRef.current(`errors.${p.code}`, {
    ...(p.params || {}), defaultValue: p.message || p.error || p.code,
  }), []);

  const label = useCallback((name) => {
    const field = fields.find(f => f.name === name);
    return field ? (he ? field.label_he : field.label_en) : (name || '—');
  }, [fields, he]);

  const loadHistory = useCallback(() => Promise.resolve(api.get('/imports', { params: { limit: 10 } }))
    .then(res => setHistory(res?.data?.items || []))
    .catch(() => {}), []);

  useEffect(() => {
    if (!allowed) return undefined;
    let live = true;
    Promise.resolve(api.get(`/imports/schema/${kind}`))
      .then(res => { if (live) setFields(res?.data?.fields || []); })
      .catch(() => { if (live) setError(tRef.current('schemaFailed')); });
    loadHistory();
    return () => { live = false; };
  }, [allowed, kind, loadHistory]);

  const reset = () => {
    setFile(null); setBatch(null); setMapping({}); setPreview(null); setResult(null); setError('');
  };

  const discardBatch = (current) => {
    if (current && ['uploaded', 'previewed'].includes(current.status)) {
      Promise.resolve(api.delete(`/imports/${current.batch_id}`)).catch(() => {});
    }
  };

  const chooseKind = (next) => {
    if (next === kind) return;
    discardBatch(batch);
    reset();
    setParams({ kind: next });
  };

  const upload = async (chosen, sheet) => {
    setBusy('upload');
    setError('');
    setPreview(null);
    setResult(null);
    try {
      const { data } = await api.post('/imports', {
        kind, filename: chosen.name, content_base64: chosen.base64, ...(sheet ? { sheet } : {}),
      });
      setFile(chosen);
      setBatch(data);
      setMapping(data.suggested_mapping || {});
    } catch (err) {
      setBatch(null);
      setError(problemText(err?.response?.data || { code: 'uploadFailed' }));
    } finally {
      setBusy('');
    }
  };

  const changeSheet = (sheet) => {
    discardBatch(batch);
    upload(file, sheet);
  };

  const usedTwice = (() => {
    const counts = {};
    Object.values(mapping).forEach(v => { if (v !== null && v !== undefined && v !== '') counts[v] = (counts[v] || 0) + 1; });
    return Object.values(counts).some(n => n > 1);
  })();
  const missingRequired = fields.filter(f => f.required && (mapping[f.name] === null || mapping[f.name] === undefined));

  const check = async () => {
    setBusy('preview');
    setError('');
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v !== null && v !== undefined && v !== ''));
      const { data } = await api.put(`/imports/${batch.batch_id}/mapping`, {
        mapping: clean, options: { create_missing: createMissing },
      });
      setPreview(data);
      setBatch(b => ({ ...b, ...data }));
    } catch (err) {
      setError(problemText(err?.response?.data || { code: 'previewFailed' }));
    } finally {
      setBusy('');
    }
  };

  const commit = async () => {
    setBusy('commit');
    setError('');
    try {
      const { data } = await api.post(`/imports/${batch.batch_id}/commit`);
      setResult(data);
      loadHistory();
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.errors) setPreview(p => ({ ...p, errors: body.errors, error_count: body.error_count }));
      setError(problemText(body.code ? body : { code: 'commitFailed' }));
    } finally {
      setBusy('');
    }
  };

  const undo = async () => {
    setBusy('undo');
    setUndoError('');
    try {
      await api.post(`/imports/${undoTarget.batch_id}/undo`);
      if (result && result.batch_id === undoTarget.batch_id) setResult(r => ({ ...r, status: 'undone' }));
      setUndoTarget(null);
      loadHistory();
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.code === 'undoBlocked') {
        const details = [...(body.blockers || []).map(b => t('undoBlockedBy', { count: b.count, table: t(`tables.${b.table}`, { defaultValue: b.table }) })),
                         ...(body.changed || []).map(c => t('undoChanged', { count: c.count, table: t(`tables.${c.table}`, { defaultValue: c.table }) }))];
        setUndoError(t('errors.undoBlocked', { details: details.join('; ') }));
      } else {
        setUndoError(problemText(body.code ? body : { code: 'undoFailed' }));
      }
    } finally {
      setBusy('');
    }
  };

  const downloadTemplate = () => {
    downloadText(`pitriot-${kind}-template.csv`, templateCsv(fields, i18n.language));
  };

  if (!allowed) {
    return (
      <div style={pageStyle}>
        <h2 style={titleStyle}>{t('title')}</h2>
        <p style={noticeStyle}>{t('notPermitted')}</p>
      </div>
    );
  }

  const columns = batch?.columns || [];
  const errors = preview?.errors || [];
  // A PITAX analysis already recorded is skipped, not imported again: the
  // button counts what will be, and offers nothing when that is none.
  const toImport = preview ? Math.max(0, preview.row_count - (preview.already_recorded || 0)) : 0;
  const nothingNew = !!preview && !preview.error_count && toImport === 0;

  return (
    <div style={pageStyle}>
      <h2 style={titleStyle}>⬆ {t('title')}</h2>
      <p style={introStyle}>{t('intro')}</p>

      <section style={cardStyle}>
        <h3 style={h3Style}>1. {t('step1')}</h3>
        <div role="group" aria-label={t('step1')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {KINDS.map(k => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => chooseKind(k)} title={t('tip.kind')} style={chip(kind === k)}>
              {t(`kinds.${k}`)}
            </button>
          ))}
        </div>
        <p style={mutedStyle}>{t(`kindHelp.${kind}`)}</p>
        <button type="button" onClick={downloadTemplate} disabled={!fields.length} title={t('tip.template')} style={linkBtn}>
          ⬇ {t('template')}
        </button>
        <div style={{ marginTop: 10 }}>
          <FileDropzone disabled={busy === 'upload'} onFile={chosen => { discardBatch(batch); upload(chosen); }}
                        {...(isPitax ? { accept: PITAX_ACCEPTED, hint: t('dropzone.hintPitax'),
                                         wrongType: t('dropzone.wrongTypePitax'), tip: t('tip.dropzonePitax') } : {})} />
        </div>
        {busy === 'upload' && <p style={mutedStyle}>{t('uploading')}</p>}
        {batch && (
          <div data-testid="import-file-summary" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
            <strong dir="ltr">{batch.filename}</strong>
            <span>{t('rowsFound', { count: batch.row_count })}</span>
            {/* PITAX's package has no header row of its own to point at: the
                server builds its table from PITAX's sheets. */}
            {!batch.pitax && <span style={mutedStyle}>{t('headerRow', { row: batch.header_row })}</span>}
            {!isPitax && (batch.sheets || []).length > 1 && (
              <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {t('sheet')}
                <select value={batch.sheet || ''} onChange={e => changeSheet(e.target.value)} title={t('tip.sheet')} style={selectStyle}>
                  {batch.sheets.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
          </div>
        )}
        {batch?.pitax && <PitaxSummary info={batch.pitax} t={t} />}
      </section>

      {error && <p role="alert" style={errorStyle}>{error}</p>}

      {batch && !result && (
        <section style={cardStyle} data-testid="import-mapping">
          <h3 style={h3Style}>2. {t('step2')}</h3>
          <div className="table-wrap">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>{t('mapping.field')}</th>
                  <th style={thStyle}>{t('mapping.column')}</th>
                  <th style={thStyle}>{t('mapping.sample')}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(f => {
                  const index = mapping[f.name];
                  const column = columns.find(c => c.index === index);
                  return (
                    <tr key={f.name}>
                      <td style={tdStyle}>
                        {he ? f.label_he : f.label_en}
                        {f.required && <span style={requiredStyle}> *</span>}
                      </td>
                      <td style={tdStyle}>
                        <select aria-label={he ? f.label_he : f.label_en} value={index ?? ''} title={t('tip.mapColumn')} style={selectStyle}
                                onChange={e => { setPreview(null); setMapping(m => ({ ...m, [f.name]: e.target.value === '' ? null : Number(e.target.value) })); }}>
                          <option value="">{t('mapping.notInFile')}</option>
                          {columns.map(c => (
                            <option key={c.index} value={c.index}>{columnLetter(c.index)} · {c.header || t('mapping.noHeader')}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--c-text-muted-fg)' }} dir="auto">
                        {column ? column.samples.map(String).join(' · ') : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {missingRequired.length > 0 && (
            <p style={warnStyle}>{t('mapping.requiredMissing', { fields: missingRequired.map(f => (he ? f.label_he : f.label_en)).join(', ') })}</p>
          )}
          {usedTwice && <p style={warnStyle}>{t('mapping.columnTwice')}</p>}
          <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13, margin: '8px 0' }}>
            <input type="checkbox" checked={createMissing} onChange={e => { setPreview(null); setCreateMissing(e.target.checked); }} title={t('tip.createMissing')} />
            <span>{t(`createMissing.${kind}`)}</span>
          </label>
          <button type="button" data-testid="import-check" onClick={check} title={t('tip.check')}
                  disabled={busy !== '' || usedTwice || missingRequired.length > 0} style={primaryBtn(busy === '' && !usedTwice && !missingRequired.length)}>
            {busy === 'preview' ? t('checking') : t('check')}
          </button>
        </section>
      )}

      {preview && !result && (
        <section style={cardStyle} data-testid="import-preview">
          <h3 style={h3Style}>3. {t('step3')}</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14, marginBottom: 8 }}>
            <span>{t('summary.rows', { count: preview.row_count })}</span>
            <span style={{ color: 'var(--c-success-fg)' }}>{t('summary.valid', { count: preview.valid_count })}</span>
            <span style={{ color: preview.error_count ? 'var(--c-danger-fg)' : 'var(--c-text-muted-fg)' }} data-testid="import-error-count">
              {t('summary.errors', { count: preview.error_count })}
            </span>
          </div>

          {preview.already_recorded > 0 && (
            <p style={mutedStyle} data-testid="import-already-recorded">{t('alreadyRecorded', { count: preview.already_recorded })}</p>
          )}

          {Object.keys(preview.will_create || {}).length > 0 && (
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <strong>{t('willCreate')}</strong>{' '}
              {Object.entries(preview.will_create).map(([table, n]) => `${t(`tables.${table}`, { defaultValue: table })}: ${n}`).join(' · ')}
            </div>
          )}

          {errors.length > 0 && (
            <>
              <h4 style={h4Style}>{t('errorsTitle')}</h4>
              <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
                <table style={tableStyle} data-testid="import-errors">
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('col.row')}</th>
                      <th style={thStyle}>{t('col.field')}</th>
                      <th style={thStyle}>{t('col.problem')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={`${e.row}-${e.field}-${i}`}>
                        <td style={tdStyle}>{e.row ?? '—'}</td>
                        <td style={tdStyle}>{e.field ? label(e.field) : '—'}</td>
                        <td style={tdStyle}>{problemText(e)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.error_count > errors.length && <p style={mutedStyle}>{t('moreErrors', { count: preview.error_count - errors.length })}</p>}
              <p style={warnStyle}>{t('fixAndRetry')}</p>
            </>
          )}

          {(preview.preview_rows || []).length > 0 && (
            <>
              <h4 style={h4Style}>{t('previewTitle')}</h4>
              <div className="table-wrap">
                <table style={tableStyle} data-testid="import-preview-rows">
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('col.row')}</th>
                      {fields.filter(f => mapping[f.name] !== null && mapping[f.name] !== undefined).map(f => (
                        <th key={f.name} style={thStyle}>{he ? f.label_he : f.label_en}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview_rows.map(r => (
                      <tr key={r.row} style={r.ok ? undefined : { background: 'var(--c-danger-soft)' }}>
                        <td style={tdStyle}>{r.row}</td>
                        {fields.filter(f => mapping[f.name] !== null && mapping[f.name] !== undefined).map(f => (
                          <td key={f.name} style={tdStyle} dir="auto">{r.values[f.name] === undefined || r.values[f.name] === null ? '' : String(r.values[f.name])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <p style={mutedStyle}>{t('allOrNothing')}</p>
          {nothingNew && <p style={mutedStyle} data-testid="import-nothing-new">{t('nothingNew')}</p>}
          <button type="button" data-testid="import-commit" onClick={commit} title={t('tip.commit')}
                  disabled={busy !== '' || preview.error_count > 0 || nothingNew}
                  style={primaryBtn(busy === '' && !preview.error_count && !nothingNew)}>
            {busy === 'commit' ? t('committing') : t('commit', { count: toImport })}
          </button>
        </section>
      )}

      {result && (
        <section style={cardStyle} data-testid="import-result">
          <h3 style={h3Style}>4. {result.status === 'undone' ? t('undone') : t('step4')}</h3>
          <p style={{ fontSize: 14 }}>
            {Object.entries(result.created_counts || {}).map(([table, n]) => `${t(`tables.${table}`, { defaultValue: table })}: ${n}`).join(' · ')}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {result.status === 'committed' && (
              <button type="button" onClick={() => { setUndoError(''); setUndoTarget(result); }} title={t('tip.undo')} style={dangerBtn}>{t('undo')}</button>
            )}
            <button type="button" onClick={reset} title={t('tip.another')} style={linkBtn}>{t('another')}</button>
          </div>
        </section>
      )}

      <section style={cardStyle}>
        <h3 style={h3Style}>{t('history.title')}</h3>
        {history.length === 0 ? <p style={mutedStyle}>{t('history.empty')}</p> : (
          <div className="table-wrap">
            <table style={tableStyle} data-testid="import-history">
              <thead>
                <tr>
                  <th style={thStyle}>{t('history.file')}</th>
                  <th style={thStyle}>{t('history.kind')}</th>
                  <th style={thStyle}>{t('history.status')}</th>
                  <th style={thStyle}>{t('history.rows')}</th>
                  <th style={thStyle}>{t('history.when')}</th>
                  <th style={thStyle}>{t('history.by')}</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.batch_id}>
                    <td style={tdStyle} dir="ltr">{h.filename}</td>
                    <td style={tdStyle}>{t(`kinds.${h.kind}`)}</td>
                    <td style={tdStyle}>{t(`history.statuses.${h.status}`)}</td>
                    <td style={tdStyle}>{h.row_count}</td>
                    <td style={tdStyle}>{(h.committed_at || h.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td style={tdStyle}>{h.created_by_username}</td>
                    <td style={tdStyle}>
                      {h.status === 'committed' && (
                        <button type="button" onClick={() => { setUndoError(''); setUndoTarget(h); }} title={t('tip.undo')} style={smallDanger}>{t('undo')}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDeleteModal
        open={Boolean(undoTarget)}
        title={t('undoTitle')}
        consequences={undoTarget ? [t('undoBody', { file: undoTarget.filename }), t('undoKeeps')] : []}
        deleting={busy === 'undo'}
        error={undoError}
        confirmLabel={t('undo')}
        confirmingLabel={t('undoing')}
        onConfirm={undo}
        onCancel={() => { if (busy !== 'undo') setUndoTarget(null); }}
      />
    </div>
  );
}

/*
 * What the server read from a PITAX package: how many consensus sequences,
 * how many with a taxonomic summary (the rows), how many not analysed yet
 * (skipped), which PITAX made it, and summaries that named no sequence
 * (left out). unmatched_summaries is only the first 50 names;
 * unmatched_count is how many there are.
 */
function PitaxSummary({ info, t }) {
  const version = info.run_info?.application_version;
  const unmatched = info.unmatched_count ?? (info.unmatched_summaries || []).length;
  const unanalysed = info.unanalysed_count ?? 0;
  return (
    <div data-testid="import-pitax-summary" style={{ fontSize: 13, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span>
        {t('pitax.sequences', { count: info.consensus_count ?? 0, identified: info.identified_count ?? 0 })}
        {version && <> · <bdi dir="ltr">{t('pitax.version', { version })}</bdi></>}
      </span>
      {unanalysed > 0 && <span data-testid="import-pitax-unanalysed">{t('pitax.unanalysed', { count: unanalysed })}</span>}
      {unmatched > 0 && <span style={{ color: 'var(--c-warning-text-fg)' }}>{t('pitax.unmatched', { count: unmatched })}</span>}
      <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('pitax.rowsNote')}</span>
    </div>
  );
}

const pageStyle = { maxWidth: 1100, margin: '0 auto', padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 12 };
const titleStyle = { margin: 0, color: 'var(--c-primary-fg)' };
const introStyle = { fontSize: 14, color: 'var(--c-text-body-fg)', margin: 0, lineHeight: 1.5, maxWidth: 760 };
const cardStyle = { background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10, padding: 14 };
const h3Style = { margin: '0 0 8px', fontSize: 16 };
const h4Style = { margin: '10px 0 4px', fontSize: 14 };
const mutedStyle = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '4px 0' };
const noticeStyle = { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '8px 12px' };
const warnStyle = { fontSize: 13, color: 'var(--c-warning-text-fg)', margin: '6px 0' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '8px 12px', margin: 0 };
const requiredStyle = { color: 'var(--c-danger-fg)', fontWeight: 700 };
const tableStyle = { borderCollapse: 'collapse', fontSize: 13, width: '100%' };
const thStyle = { textAlign: 'start', padding: '4px 8px', borderBottom: '1px solid var(--c-border)', color: 'var(--c-text-muted-fg)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '4px 8px', borderBottom: '1px solid var(--c-bg-muted)', verticalAlign: 'top' };
const selectStyle = { fontSize: 13, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--c-border-strong)', maxWidth: 260 };
const chip = on => ({ fontSize: 14, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: on ? 600 : 400,
                      border: `2px solid ${on ? 'var(--c-primary)' : 'var(--c-border)'}`, background: on ? 'var(--c-primary-soft)' : 'var(--c-surface)', color: 'var(--c-text-fg)' });
const primaryBtn = enabled => ({ background: enabled ? 'var(--c-primary)' : 'var(--c-text-faint)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
                                 padding: '8px 16px', cursor: enabled ? 'pointer' : 'default', fontSize: 14 });
const linkBtn = { background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 };
const dangerBtn = { background: 'var(--c-danger-2)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 14 };
const smallDanger = { background: 'var(--c-surface)', color: 'var(--c-danger-2-fg)', border: '1px solid var(--c-danger-border)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12 };
